import React, { useState, useEffect } from 'react';
import './SmartAppBanner.css';
import { isAndroid } from '../utils/deviceDetection';
import { openCurrentPageInApp, getPlayStoreUrl } from '../utils/deepLinkUtils';

const BANNER_DISMISSED_KEY = 'smart_app_banner_dismissed';
const DISMISS_DURATION_DAYS = 7;

const SmartAppBanner = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);

    useEffect(() => {
        // Check if banner should be shown
        const shouldShowBanner = () => {
            // Only show on Android devices
            if (!isAndroid()) {
                return false;
            }

            // Check if banner was dismissed recently
            const dismissedAt = localStorage.getItem(BANNER_DISMISSED_KEY);
            if (dismissedAt) {
                const dismissedDate = new Date(dismissedAt);
                const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

                if (daysSinceDismissed < DISMISS_DURATION_DAYS) {
                    return false;
                }
            }

            return true;
        };

        if (shouldShowBanner()) {
            setIsVisible(true);
            document.body.classList.add('smart-app-banner-visible');
        }

        return () => {
            document.body.classList.remove('smart-app-banner-visible');
        };
    }, []);

    const handleOpenInApp = async () => {
        setIsLoading(true);

        try {
            const appOpened = await openCurrentPageInApp();

            if (!appOpened) {
                // App didn't open - show install option
                setShowInstallPrompt(true);
            }
        } catch (error) {
            console.error('Error opening app:', error);
            setShowInstallPrompt(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleContinueInBrowser = () => {
        setIsVisible(false);
        document.body.classList.remove('smart-app-banner-visible');
        localStorage.setItem(BANNER_DISMISSED_KEY, new Date().toISOString());
    };

    const handleClose = () => {
        setIsVisible(false);
        document.body.classList.remove('smart-app-banner-visible');
        localStorage.setItem(BANNER_DISMISSED_KEY, new Date().toISOString());
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className="smart-app-banner">
            <div className="smart-app-banner__content">
                <div className="smart-app-banner__icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.9 10.9C17.64 10.9 17.4 10.75 17.3 10.5L14.5 3.5C14.36 3.16 14.5 2.77 14.84 2.62C15.18 2.47 15.57 2.64 15.72 2.97L18.5 9.97C18.64 10.31 18.47 10.7 18.13 10.85C18.05 10.88 17.97 10.9 17.9 10.9M10.1 10.9C10.03 10.9 9.95 10.88 9.87 10.85C9.53 10.7 9.36 10.31 9.5 9.97L12.28 2.97C12.43 2.63 12.82 2.47 13.16 2.62C13.5 2.77 13.67 3.16 13.53 3.5L10.75 10.5C10.65 10.75 10.41 10.9 10.15 10.9M2 12C2 9.21 4.21 7 7 7H9.5V9H7C5.34 9 4 10.34 4 12V17C4 18.66 5.34 20 7 20H17C18.66 20 20 18.66 20 17V12C20 10.34 18.66 9 17 9H14.5V7H17C19.79 7 22 9.21 22 12V17C22 19.79 19.79 22 17 22H7C4.21 22 2 19.79 2 17V12Z" />
                    </svg>
                </div>
                <div className="smart-app-banner__text">
                    <p className="smart-app-banner__title">
                        {showInstallPrompt ? 'App Not Installed' : 'Get our App'}
                    </p>
                    <p className="smart-app-banner__subtitle">
                        {showInstallPrompt ? 'Install from Play Store' : 'Better experience on mobile'}
                    </p>
                </div>
            </div>
            <div className="smart-app-banner__actions">
                {showInstallPrompt ? (
                    <>
                        <button
                            className="smart-app-banner__button smart-app-banner__button--primary"
                            onClick={() => window.open(getPlayStoreUrl(), '_blank')}
                        >
                            Install App
                        </button>
                        <button
                            className="smart-app-banner__button smart-app-banner__button--secondary"
                            onClick={() => setShowInstallPrompt(false)}
                        >
                            Back
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            className="smart-app-banner__button smart-app-banner__button--primary"
                            onClick={handleOpenInApp}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Opening...' : 'Open in App'}
                        </button>
                        <button
                            className="smart-app-banner__button smart-app-banner__button--secondary"
                            onClick={handleContinueInBrowser}
                        >
                            Continue in Browser
                        </button>
                    </>
                )}
                <button
                    className="smart-app-banner__close"
                    onClick={handleClose}
                    aria-label="Close banner"
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default SmartAppBanner;
